import {
  css, type SerializedStyles,
} from '@emotion/react';
import {
  faLock, faShieldCheck, faUnlock,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type ComponentProps, type FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { FormTextInputComponent } from '~/_shared/baseComponents/inputs';
import { radioIndicatorDim } from '~/_shared/baseComponents/radio/radio.component';
import { RadioGroupComponent } from '~/_shared/baseComponents/radio/radioGroup.component';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useTheme } from '../../themes/theme.hooks';
import { MapPrivacyLevel } from '../../types/map';
import { type ThemeProps } from '../../types/themeProps';
import { useTranslation } from '../../utils/hooks';
import {
  createPasswordErrorMessage, validatePassword as isPasswordValid, validatePasswords,
} from '../../utils/throttle/validation/validation.helpers';
import { PasswordStrengthIndicatorComponent } from '../passwordStrengthIndicator/passwordStrengthIndicator.component';
import { PasswordOption } from './mapPrivacySelector.types';
import { PasswordOptionSelectorComponent } from './passwordOptionSelector.component';

const EMPTY_PASSWORD_ERRORS: string[] = [];

const accordionLabelStyle = css({
  textTransform: 'uppercase',
  fontSize: '16px',
});

const inputStyle = ({ theme, isValid }: ThemeProps<{ isValid: boolean }>) => css({
  borderColor: isValid ? theme.lineColors.green : undefined,
});

const confirmPasswordContainerStyle = css({
  marginTop: 5,
});

const iconStyle = css({
  fontSize: 28,
  width: 25,
});

const containerStyle = css({
  width: '100%',
});

const alertContainerStyle = css({
  marginTop: 15,
  width: '100%',
});

const passwordFormStyle = css({
  marginTop: 10,
});

const passwordOptionGroupStyle = css({
  margin: `12px 0 0 ${2 * radioIndicatorDim}px`,
});

const animatedRevealWrapperStyle = (isVisible: boolean) => css({
  display: 'grid',
  gridTemplateRows: isVisible ? '1fr' : '0fr',
  transition: 'grid-template-rows 0.2s ease-in-out',
});

const animatedRevealInnerStyle = css({
  overflow: 'hidden',
});

type MapPrivacySelectorProps = Readonly<{
  disabledPrivacyLevels?: ReadonlyArray<MapPrivacyLevel>;
  globalError?: string;
  initialPasswordOption?: PasswordOption;
  isPasswordProtected?: boolean;
  passwordError?: string;
  preservePasswordDisabledReason?: string;
  skipDisabledTooltipShowTimeout?: boolean;
  privacyLevel: MapPrivacyLevel | null;
  radioGroupStyle?: SerializedStyles;
  showPrivate?: boolean;

  onChange: (newPrivacyLevel: MapPrivacyLevel, password?: string) => void;
  onClearGlobalError?: () => void;
  onClearPasswordError?: () => void;
  onIsFormValidChanged?: (isDisabled: boolean) => void;
  onPasswordOptionChange?: (option: PasswordOption) => void;
}>;

export const MapPrivacySelectorComponent: FC<MapPrivacySelectorProps> = ({
  onChange,
  onClearPasswordError,
  onIsFormValidChanged,
  onPasswordOptionChange,
  ...props
}) => {
  const [t] = useTranslation();
  const theme = useTheme();

  const allowedPasswordOptions = useMemo(() => {
    const allowedOptions = new Set([PasswordOption.create]);

    if (!props.preservePasswordDisabledReason) {
      allowedOptions.add(PasswordOption.preserve);
    }
    return allowedOptions;
  }, [props.preservePasswordDisabledReason]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showIfPasswordsMatch, setShowIfPasswordsMatch] = useState(false);
  const [passwordErrorMessages, setPasswordErrorMessages] = useState<string[]>([]);
  const [passwordOption, setPasswordOption] = useState<PasswordOption | null>(() => {
    if (props.initialPasswordOption && allowedPasswordOptions.has(props.initialPasswordOption)) {
      return props.initialPasswordOption;
    }
    return null;
  });
  const [originalPrivacyLevel] = useState(props.privacyLevel);

  const validateConfirmPassword = useCallback(() => {
    if (password !== confirmPassword) {
      setShowIfPasswordsMatch(true);
    }
  }, [confirmPassword, password]);

  const validatePassword = useCallback(() => {
    setPasswordErrorMessages(createPasswordErrorMessage(password, t));
    if (confirmPassword && password !== confirmPassword) {
      setShowIfPasswordsMatch(true);
    }
  }, [confirmPassword, password, t]);

  const submitPasswordIfValid = useCallback(({ password, confirmPassword }: Readonly<{ password: string; confirmPassword: string }>) => {
    onIsFormValidChanged?.(password === confirmPassword && validatePasswords(password, confirmPassword));
    onClearPasswordError?.();

    if (props.privacyLevel && password === confirmPassword && validatePasswords(password, confirmPassword)) {
      onChange(props.privacyLevel, password);
    }
  }, [onIsFormValidChanged, onClearPasswordError, props.privacyLevel, onChange]);

  const onPrivacyLevelChange = useCallback((privacyLevel: string) => {
    let newPrivacyLevel;
    setShowIfPasswordsMatch(true);

    switch (privacyLevel) {
      case MapPrivacyLevel.Public.toString():
        newPrivacyLevel = (MapPrivacyLevel.Public);
        break;
      case MapPrivacyLevel.PasswordProtected.toString():
        newPrivacyLevel = (MapPrivacyLevel.PasswordProtected);
        break;
      case MapPrivacyLevel.Private.toString():
        newPrivacyLevel = (MapPrivacyLevel.Private);
        break;
      default:
        return;
    }

    if (newPrivacyLevel !== MapPrivacyLevel.PasswordProtected && !props.initialPasswordOption) {
      setPasswordOption(null);
      setPassword('');
      setConfirmPassword('');
    }

    if (newPrivacyLevel === MapPrivacyLevel.PasswordProtected && props.isPasswordProtected) {
      onChange(newPrivacyLevel);
    }
    else {
      onChange(newPrivacyLevel, password);
    }

    onClearPasswordError?.();
    onIsFormValidChanged?.(newPrivacyLevel !== MapPrivacyLevel.PasswordProtected);
  }, [props.initialPasswordOption, props.isPasswordProtected, onChange, onClearPasswordError, onIsFormValidChanged, password]);

  const handlePasswordOptionChange = useCallback((value: PasswordOption) => {
    setPasswordOption(value);
    onClearPasswordError?.();
    onPasswordOptionChange?.(value);
    onIsFormValidChanged?.(value === PasswordOption.preserve);
  }, [onClearPasswordError, onIsFormValidChanged, onPasswordOptionChange]);

  const errorMessages = useMemo(() =>
    createPasswordErrorMessage(password, t),
  [password, t]);

  useEffect(() => {
    if (!password.length || isPasswordValid(password)) {
      setPasswordErrorMessages(EMPTY_PASSWORD_ERRORS);
    }
    else {
      setPasswordErrorMessages(errorMessages);
    }
  }, [errorMessages, password, passwordErrorMessages, t]);

  useEffect(() => {
    if (props.initialPasswordOption) {
      setPasswordOption(props.initialPasswordOption);
    }
  }, [props.initialPasswordOption]);

  // prevent disabled password options from being active
  useEffect(() => {
    if (!passwordOption || allowedPasswordOptions.has(passwordOption)) {
      return;
    }

    const firstAllowedOption = Array.from(allowedPasswordOptions.values())[0] ?? null;
    setPasswordOption(firstAllowedOption);
  }, [passwordOption, allowedPasswordOptions]);

  const passwordErrors = useMemo(() =>
    props.passwordError ? [props.passwordError, ...passwordErrorMessages] : passwordErrorMessages,
  [passwordErrorMessages, props.passwordError]);

  useEffect(() => {
    if (showIfPasswordsMatch && password === confirmPassword) {
      setShowIfPasswordsMatch(false);
    }
  }, [showIfPasswordsMatch, password, confirmPassword]);

  const isValid = validatePasswords(password, confirmPassword);

  const passwordInputPlaceholder = originalPrivacyLevel === MapPrivacyLevel.PasswordProtected ? '******' : t('Enter password');

  const PasswordForm = (
    <form css={passwordFormStyle}>
      <FormTextInputComponent
        inputStyle={inputStyle({ theme, isValid })}
        onChange={newPassword => {
          setPassword(newPassword);
          submitPasswordIfValid({ password: newPassword, confirmPassword });
        }}
        value={password}
        type="password"
        disablePasswordManagers
        placeholder={passwordInputPlaceholder}
        onBlur={validatePassword}
        errorMessages={passwordErrors}
        autoComplete="new-password"
        icon={faLock}
      />
      <PasswordStrengthIndicatorComponent password={password} />

      <div css={confirmPasswordContainerStyle}>
        <FormTextInputComponent
          inputStyle={inputStyle({ theme, isValid })}
          onChange={newConfirmPassword => {
            setConfirmPassword(newConfirmPassword);
            submitPasswordIfValid({ password, confirmPassword: newConfirmPassword });
          }}
          value={confirmPassword}
          type="password"
          disablePasswordManagers
          placeholder={passwordInputPlaceholder}
          onBlur={validateConfirmPassword}
          errorMessages={showIfPasswordsMatch ? [t('Passwords do not match')] : EMPTY_PASSWORD_ERRORS}
          icon={faLock}
        />
      </div>
    </form>
  );

  const PasswordSection = props.isPasswordProtected
    ? (
      <div css={passwordOptionGroupStyle}>
        <PasswordOptionSelectorComponent
          onValueChange={handlePasswordOptionChange}
          selectedValue={passwordOption}
          preservePasswordDisabledReason={props.preservePasswordDisabledReason}
          skipDisabledTooltipShowTimeout={props.skipDisabledTooltipShowTimeout}
        />
        <div css={animatedRevealWrapperStyle(passwordOption === PasswordOption.create)}>
          <div
            css={animatedRevealInnerStyle}
            inert={passwordOption === PasswordOption.create ? undefined : true}
          >
            {PasswordForm}
          </div>
        </div>
      </div>
    )
    : PasswordForm;

  const items: ComponentProps<typeof RadioGroupComponent>['items'] = [props.showPrivate ? {
    label: <span css={accordionLabelStyle}>{t('Private')}</span>,
    description: t('Only people with the login to your Maptive account can view the map.'),
    value: MapPrivacyLevel.Private.toString(),
    icon: (
      <FontAwesomeIcon
        css={iconStyle}
        icon={faShieldCheck}
      />
    ),
    isDisabled: props.disabledPrivacyLevels && props.disabledPrivacyLevels.includes(MapPrivacyLevel.Private),
  } : null, {
    label: <span css={accordionLabelStyle}>{t('Password Protected')}</span>,
    description: t('Only people with the password can view.'),
    value: MapPrivacyLevel.PasswordProtected.toString(),
    icon: (
      <FontAwesomeIcon
        css={iconStyle}
        icon={faLock}
      />
    ),
    children: (() => {
      const isPasswordSectionVisible = props.privacyLevel === MapPrivacyLevel.PasswordProtected
        && (!props.disabledPrivacyLevels || !props.disabledPrivacyLevels.includes(MapPrivacyLevel.PasswordProtected));

      return (
        <div css={animatedRevealWrapperStyle(isPasswordSectionVisible)}>
          <div
            css={animatedRevealInnerStyle}
            inert={isPasswordSectionVisible ? undefined : true}
          >
            {PasswordSection}
          </div>
        </div>
      );
    })(),
    isDisabled: props.disabledPrivacyLevels && props.disabledPrivacyLevels.includes(MapPrivacyLevel.PasswordProtected),
  }, {
    label: <span css={accordionLabelStyle}>{t('Public')}</span>,
    description: t('Anyone with the link can view.'),
    value: MapPrivacyLevel.Public.toString(),
    icon: (
      <FontAwesomeIcon
        css={iconStyle}
        icon={faUnlock}
      />
    ),
    isDisabled: props.disabledPrivacyLevels && props.disabledPrivacyLevels.includes(MapPrivacyLevel.Public),
  }].filter(notNullsy);

  return (
    <div css={containerStyle}>
      <RadioGroupComponent
        css={props.radioGroupStyle}
        selectedValue={props.privacyLevel?.toString() || ''}
        onValueChange={onPrivacyLevelChange}
        items={items}
      />
      {!!props.globalError && (
        <div css={alertContainerStyle}>
          <AlertComponent
            type="danger"
            onClose={props.onClearGlobalError}
          >
            {props.globalError}
          </AlertComponent>
        </div>
      )}
    </div>
  );
};
